import {
  DecoratedSilo,
  SiloAsset,
  getCollateralAssets,
  getDebtAssets,
  getHasDebt,
} from '../../../queries/decoratedSilo';
import { useThisDecoratedSilo } from '../silo';
import { useActionFormContext } from '../common/ActionForm';
import { FC } from 'react';
import { getLiquidationPriceFromLtv } from '../common/PositionsPanel/utilities/Positions';
import { useQueryQuotePrice } from '../../../queries/price/useQueryQuotePrice';
import { P, match } from 'ts-pattern';
import { LTV, calculateLTV, mapToCollateralAsset, mapToDebtAsset } from '../../util/ltv';
import { BigNumber } from 'ethers';
import { BIG_ZERO } from '../../../constants';
import produce from 'immer';
import { getNewLTV } from './getNewLtv';
import { NATIVE_CURRENCY_DECIMALS } from '@silo/types';
import { asFullUSD, convertCurrency } from '@silo/utils';
import { KeyValueList, useDescriptionVariant } from '@silo/ui';

export const LiquidationPriceSimulator: FC = () => {
  const decoratedSilo = useThisDecoratedSilo();
  const collateralAssets = getCollateralAssets(decoratedSilo);
  const debtAssets = getDebtAssets(decoratedSilo);
  const { selectedAsset, normalizedTokenAmount, currentLTV } = useActionFormContext();
  const assetsToDisplay = dedupAssets([...collateralAssets, ...debtAssets, selectedAsset]);
  const ltv = calculateNewLtv({
    currentLTV,
    decoratedSilo,
    selectedAsset,
    normalizedTokenAmount,
  });

  const isLoanAdjustment = !!currentLTV && !!normalizedTokenAmount;

  return (
    <KeyValueList>
      {assetsToDisplay.map((asset, i) => (
        <LiquidationPriceAsset
          asset={asset}
          ltv={ltv}
          isLoanAdjustment={isLoanAdjustment}
          isLast={i === assetsToDisplay.length - 1}
        />
      ))}
    </KeyValueList>
  );
};

interface LiquidationPriceAssetProps {
  asset: SiloAsset;
  ltv: LTV;
  isLoanAdjustment: boolean;
  isLast: boolean;
}

function LiquidationPriceAsset({ asset, ltv, isLoanAdjustment, isLast }: LiquidationPriceAssetProps) {
  const quotePrice = useQueryQuotePrice();
  const liquidationPrice = getLiquidationPriceFromLtv(ltv, asset.assetAddress);
  const { selectedAddress } = useActionFormContext();
  const hasDebt = getHasDebt(asset);
  const isNewDebtAsset = asset.assetAddress === selectedAddress && !hasDebt;
  const showNewLabel = isLoanAdjustment && !isNewDebtAsset;
  const descriptionVariant = useDescriptionVariant();
  const description =
    descriptionVariant === 'static' && !isLast ? undefined : (
      <p>This is the simulated asset price at which the borrow position becomes insolvent (HF goes to 0%).</p>
    );

  return (
    <KeyValueList.Pair description={description}>
      <KeyValueList.Key>
        {showNewLabel ? 'New ' : ''}
        {asset.symbol} liquidation price
      </KeyValueList.Key>

      <KeyValueList.Value>
        {match({ quotePrice, liquidationPrice })
          .with(
            {
              quotePrice: P.not(P.nullish),
              liquidationPrice: { _tag: 'applicable' },
            },
            ({ quotePrice, liquidationPrice: { value } }) => {
              const rawUsd = convertCurrency({ value, decimals: NATIVE_CURRENCY_DECIMALS }, quotePrice);

              return asFullUSD(rawUsd);
            }
          )
          .otherwise(() => '--')}
      </KeyValueList.Value>
    </KeyValueList.Pair>
  );
}

function dedupAssets(assets: SiloAsset[]): SiloAsset[] {
  const map = new Map<string, SiloAsset>();

  for (const asset of assets) {
    map.set(asset.assetAddress, asset);
  }

  return [...map.values()];
}

type CalculateNewLtv = (params: {
  currentLTV: LTV | null;
  decoratedSilo: DecoratedSilo;
  normalizedTokenAmount: BigNumber | null;
  selectedAsset: SiloAsset;
}) => LTV;

const calculateNewLtv: CalculateNewLtv = ({ currentLTV, decoratedSilo, normalizedTokenAmount, selectedAsset }) => {
  if (!currentLTV) {
    const collateralAssets = getCollateralAssets(decoratedSilo);
    const debtBalance = normalizedTokenAmount || BIG_ZERO;
    const debtAsset = produce(selectedAsset, (draft) => {
      draft.debtBalance = debtBalance;
    });

    return calculateLTV({
      collateralAssets: collateralAssets.map(mapToCollateralAsset),
      debtAssets: [mapToDebtAsset(debtAsset)],
    });
  }

  if (!normalizedTokenAmount) return currentLTV;

  return getNewLTV({
    currentLTV,
    normalizedTokenAmount,
    selectedAsset,
  });
};
